import type { LucideIcon } from 'lucide-react';
import {
  LayoutGrid,
  GraduationCap,
  BarChart3,
  Compass,
  Cpu,
  ExternalLink,
  Sparkles,
  Wrench,
  Archive,
  FlaskConical,
} from 'lucide-react';

export type LabCatalogKindId =
  | 'app'
  | 'coach'
  | 'insights'
  | 'clarity'
  | 'engine'
  | 'archive'
  | 'external'
  | 'interactive'
  | 'utility'
  | 'experiment';

export interface LabCatalogKind {
  id: LabCatalogKindId;
  label: string;
  icon: LucideIcon;
  iconClass: string;
}

export const LAB_CATALOG_KINDS: LabCatalogKind[] = [
  {
    id: 'app',
    label: 'Standalone app',
    icon: LayoutGrid,
    iconClass: 'text-consulting-slate-blue-600',
  },
  {
    id: 'coach',
    label: "Coach's tool",
    icon: GraduationCap,
    iconClass: 'text-purple-600',
  },
  {
    id: 'insights',
    label: 'Prioritization insights',
    icon: BarChart3,
    iconClass: 'text-teal-600',
  },
  {
    id: 'clarity',
    label: 'Self-discovery & clarity',
    icon: Compass,
    iconClass: 'text-consulting-slate-blue-500',
  },
  {
    id: 'engine',
    label: 'Engine lab tool',
    icon: Cpu,
    iconClass: 'text-charcoal-500',
  },
  {
    id: 'archive',
    label: 'Archive / evidence',
    icon: Archive,
    iconClass: 'text-charcoal-400',
  },
  {
    id: 'external',
    label: 'External / dev server',
    icon: ExternalLink,
    iconClass: 'text-blue-600',
  },
  {
    id: 'interactive',
    label: 'In-app interactive',
    icon: Sparkles,
    iconClass: 'text-amber-600',
  },
  {
    id: 'utility',
    label: 'Utility',
    icon: Wrench,
    iconClass: 'text-charcoal-500',
  },
  {
    id: 'experiment',
    label: 'Lab experiment',
    icon: FlaskConical,
    iconClass: 'text-amber-700',
  },
];

const KIND_BY_ID = new Map(LAB_CATALOG_KINDS.map((kind) => [kind.id, kind]));

export interface LabItemKindInput {
  path: string;
  isStandalone?: boolean;
  isExternal?: boolean;
  isCoachTool?: boolean;
  isEventTrigger?: boolean;
  isSpecial?: boolean;
  targetEngine?: string;
}

/** Single organizing kind for the alphabetical catalog index. */
export function resolveLabCatalogKind(item: LabItemKindInput): LabCatalogKind {
  if (item.isExternal) return KIND_BY_ID.get('external')!;
  if (item.isEventTrigger || item.isSpecial) return KIND_BY_ID.get('interactive')!;
  if (item.isCoachTool) return KIND_BY_ID.get('coach')!;
  if (item.isStandalone) return KIND_BY_ID.get('app')!;
  if (item.targetEngine === 'Analytics Engine') return KIND_BY_ID.get('insights')!;
  if (item.targetEngine === 'Self-discovery & Clarity') return KIND_BY_ID.get('clarity')!;
  if (item.targetEngine === 'Prioritization App Archive') return KIND_BY_ID.get('archive')!;
  if (item.targetEngine === 'Utilities') return KIND_BY_ID.get('utility')!;
  if (item.targetEngine) return KIND_BY_ID.get('engine')!;
  if (item.path.startsWith('/lab/') || item.path.startsWith('/elevation/lab/')) return KIND_BY_ID.get('experiment')!;
  return KIND_BY_ID.get('experiment')!;
}
